import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';

interface ConvCredits { balance: number; unitAmount: number | null; currency: string; available: boolean }

function money(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
}

/**
 * Prepaid card-conversion credits. A conversion requires either a credit balance or the metered
 * "Card Conversion" plan (shown in Available plans above). This block covers the prepaid option.
 */
export function CardConversionCredits() {
  const credits = useQuery({ queryKey: ['conv-credits'], queryFn: () => api.get<ConvCredits>('/credits/conversion') });
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function buy() {
    setBusy(true); setErr(null);
    try {
      const res = await api.post<{ checkoutUrl: string }>('/credits/conversion/checkout', { quantity: qty });
      if (res.checkoutUrl) window.location.href = res.checkoutUrl;
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not start checkout');
    } finally { setBusy(false); }
  }

  const unit = credits.data?.unitAmount ?? null;
  const cur = credits.data?.currency ?? 'usd';

  return (
    <div>
      <div className="section-title" style={{ marginTop: 32 }}>Card conversion credits</div>
      <p className="cart-note" style={{ textAlign: 'left', marginTop: 0 }}>
        // Each card conversion needs one prepaid credit — or subscribe to the metered Card Conversion plan above.
        Credits never expire.
      </p>
      {err && <p className="cart-error">{err}</p>}
      <div className="panel" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, maxWidth: 640 }}>
        <div>
          <div className="stat-num">{credits.isLoading ? '…' : (credits.data?.balance ?? 0)}</div>
          <div className="stat-lbl">credits available</div>
        </div>
        {credits.data?.available !== false && (
          <div className="inline-form" style={{ alignItems: 'flex-end' }}>
            <label className="field" style={{ width: 110 }}>
              <span className="field-label">Quantity</span>
              {/* Override the global .input min-width:200px so the number field fits its 110px column
                  instead of overflowing and overlapping the Buy button. */}
              <input className="input" type="number" min={1} value={qty} style={{ minWidth: 0 }}
                onChange={(e) => setQty(Math.max(1, Math.floor(Number(e.target.value) || 1)))} />
            </label>
            <button className="btn btn-primary" onClick={buy} disabled={busy}>
              {busy ? 'Starting…' : unit != null ? `Buy ${qty} — ${money(unit * qty, cur)} →` : 'Buy credits →'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
